import { useEffect, useState } from "react";
import { CaPageHeader, usePath } from "@/company-admin/shared";
import { Button, Field, Input, Select, TextArea } from "@/admin/ui";
import { caApi, formatApiError } from "@/company-admin/api";

const INDUSTRIES = ["SaaS", "Retail", "F&B", "Logistics", "Fintech", "Healthcare", "Manufacturing", "Media", "Real Estate", "Education", "Other"];
const BUSINESS_TYPES = ["Private Limited", "LLP", "Sole Proprietor", "Partnership", "Public Limited", "Non-Profit"];

export default function CompanyProfile() {
    const { data, loading, refresh } = usePath("/company-admin/company");
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    useEffect(() => { if (data) setForm({ ...data }); }, [data]);

    async function save() {
        if (!form) return;
        setBusy(true); setErr(""); setMsg("");
        try {
            const payload = {
                name: form.name, logo: form.logo, industry: form.industry,
                country: form.country, currency: form.currency,
                team_size: Number(form.team_size) || 1,
                business_type: form.business_type, timezone: form.timezone,
                website: form.website, address: form.address,
                tax_number: form.tax_number, description: form.description,
                ceo_name: form.ceo_name, ceo_email: form.ceo_email,
            };
            await caApi.patch("/company-admin/company", payload);
            setMsg("Company profile saved.");
            await refresh();
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    if (loading || !form) {
        return <div data-testid="ca-company-page"><CaPageHeader title="Company Profile" /><div className="glass rounded-3xl p-8 h-[400px] animate-pulse" /></div>;
    }

    return (
        <div data-testid="ca-company-page">
            <CaPageHeader
                title="Company Profile"
                subtitle="Keep your company details up to date — CEO's GPT uses these across every workspace."
                action={
                    <div className="flex gap-2">
                        <Button variant="ghost" onClick={() => setForm({ ...data })}>Cancel</Button>
                        <Button onClick={save} disabled={busy} data-testid="ca-company-save">
                            {busy ? "Saving…" : "Save"}
                        </Button>
                    </div>
                }
            />
            {msg && <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 text-emerald-700 text-sm px-4 py-3 mb-5">{msg}</div>}
            {err && <div className="rounded-2xl border border-rose-200 bg-rose-50/60 text-rose-700 text-sm px-4 py-3 mb-5">{err}</div>}

            <div className="glass rounded-3xl p-6 sm:p-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Field label="Company Name"><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ca-company-name" /></Field>
                    <Field label="Company Logo (URL)"><Input value={form.logo || ""} onChange={(e) => setForm({ ...form, logo: e.target.value })} placeholder="https://…" /></Field>
                    <Field label="Industry">
                        <Select value={form.industry || ""} onChange={(e) => setForm({ ...form, industry: e.target.value })}>
                            <option value="">— Select —</option>
                            {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
                        </Select>
                    </Field>
                    <Field label="Business Type">
                        <Select value={form.business_type || ""} onChange={(e) => setForm({ ...form, business_type: e.target.value })}>
                            <option value="">— Select —</option>
                            {BUSINESS_TYPES.map((i) => <option key={i}>{i}</option>)}
                        </Select>
                    </Field>
                    <Field label="Country"><Input value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
                    <Field label="Currency"><Input value={form.currency || ""} onChange={(e) => setForm({ ...form, currency: e.target.value })} placeholder="INR" /></Field>
                    <Field label="Timezone"><Input value={form.timezone || ""} onChange={(e) => setForm({ ...form, timezone: e.target.value })} placeholder="Asia/Kolkata" /></Field>
                    <Field label="Team Size"><Input type="number" min="1" value={form.team_size || 1} onChange={(e) => setForm({ ...form, team_size: e.target.value })} /></Field>
                    <Field label="Website"><Input value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" /></Field>
                    <Field label="Tax Number (GST / VAT)"><Input value={form.tax_number || ""} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} /></Field>
                    <Field label="CEO Name"><Input value={form.ceo_name || ""} onChange={(e) => setForm({ ...form, ceo_name: e.target.value })} /></Field>
                    <Field label="CEO Email"><Input type="email" value={form.ceo_email || ""} onChange={(e) => setForm({ ...form, ceo_email: e.target.value })} /></Field>
                    <div className="sm:col-span-2 lg:col-span-3">
                        <Field label="Address"><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
                    </div>
                    <div className="sm:col-span-2 lg:col-span-3">
                        <Field label="Business Description"><TextArea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What does your company do?" /></Field>
                    </div>
                </div>
            </div>
        </div>
    );
}
